import { create } from "zustand";
import { INITIAL_STRATEGIES, type Strategy } from "@/mocks/operating-strategy";
import type { Shop, User } from "@/stores/auth";

export function operatingStrategyScope(user: User | null, shops: Shop[], shopId: string | null) {
  if (!user) return null;
  const validShop = shops.some(
    (shop) => shop.shopId === shopId && shop.workspaceId === user.activeWorkspaceId,
  );
  return `${user.id}:${user.activeWorkspaceId ?? "none"}:${validShop ? shopId : "none"}`;
}

interface OperatingStrategyState {
  wizardDirty: boolean;
  setWizardDirty: (dirty: boolean) => void;
  scopeKey: string | null;
  strategies: Strategy[];
  byScope: Record<string, Strategy[]>;
  setScope: (scopeKey: string | null) => void;
  addStrategy: (strategy: Strategy) => void;
  reset: () => void;
}

function initialStrategies(): Strategy[] {
  return structuredClone(INITIAL_STRATEGIES);
}

// Strategy records live only in this browser session and never reach advertising APIs.
export const useOperatingStrategyStore = create<OperatingStrategyState>((set) => ({
  wizardDirty: false,
  setWizardDirty: (wizardDirty) => set({ wizardDirty }),
  scopeKey: null,
  strategies: [],
  byScope: {},
  setScope: (scopeKey) =>
    set((state) => {
      if (scopeKey === state.scopeKey) return state;
      if (!scopeKey) return { scopeKey: null, strategies: [], byScope: {} };
      const strategies = state.byScope[scopeKey] ?? initialStrategies();
      return { scopeKey, strategies, byScope: { ...state.byScope, [scopeKey]: strategies } };
    }),
  addStrategy: (strategy) =>
    set((state) => {
      if (!state.scopeKey || strategy.source === "builtin") return state;
      if (state.strategies.some((existing) => existing.id === strategy.id)) return state;
      const strategies = [structuredClone(strategy), ...state.strategies];
      return { strategies, byScope: { ...state.byScope, [state.scopeKey]: strategies } };
    }),
  reset: () =>
    set((state) => {
      if (!state.scopeKey) return { strategies: [], byScope: {} };
      const strategies = initialStrategies();
      return { strategies, byScope: { ...state.byScope, [state.scopeKey]: strategies } };
    }),
}));
